import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react'
import { cn } from '../../lib/cn'

/**
 * Shared control surface. It sets `w-full`, so a narrower control must be wrapped rather than given
 * a width class: Tailwind emits `.w-full` after `.w-28`, and the stylesheet order — not the order of
 * the class string — decides, so `className="w-28"` would be silently ignored. `max-w-*` is safe.
 *
 *   <div className="w-32"><Input … /></div>   ✓
 *   <Input className="w-32" />                ✗ stays full width
 *
 * The font size is not part of the base: `Select`'s size variant owns it, and a base `text-sm`
 * would win over a `text-xs` passed in `className` for the same reason.
 */
export const controlBase = 'w-full rounded-lg border border-cream-300 bg-cream-50 px-3 text-ink-100 placeholder:text-ink-500 transition-shadow focus:border-ginger-500 focus:outline-none focus:ring-[3px] focus:ring-ginger-100 disabled:bg-cream-100 disabled:text-ink-500 aria-[invalid=true]:border-red-600'

export const controlClass = `${controlBase} text-[13.5px] max-sm:text-base`

/**
 * One height scale for every control, so inputs, selects, buttons and chips line up in a row:
 * `md` is 36px on desktop and 44px (a touch target) on phones; `sm` is 30px / 36px.
 */
export const CONTROL_H = { md: 'h-9 max-sm:h-11', sm: 'h-[30px] max-sm:h-9' } as const

/** A toggle chip (radio or multi-select choice) sized like a `md` control. */
export function chipClass(on: boolean): string {
  return cn(CONTROL_H.md, 'rounded-lg border px-3.5 text-[13.5px] font-medium transition-colors max-sm:text-sm',
    on ? 'border-ginger-500 bg-ginger-500 text-white hover:bg-ginger-400' : 'border-cream-300 bg-cream-50 text-ink-300 hover:bg-cream-200 hover:text-ink-100')
}

export function Field({ label, hint, error, children, id, action }: { label: ReactNode; hint?: ReactNode; error?: string; children: (id: string) => ReactNode; id?: string; action?: ReactNode }) {
  const auto = useId()
  const fid = id ?? auto
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={fid} className="text-[12.5px] font-medium text-ink-300">{label}</label>
        {action}
      </div>
      {children(fid)}
      {error ? <p className="text-xs text-red-600">{error}</p> : hint ? <p className="text-xs text-ink-500">{hint}</p> : null}
    </div>
  )
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...p }, ref) => (
  <input ref={ref} className={cn(controlClass, CONTROL_H.md, className)} {...p} />
))
Input.displayName = 'Input'

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...p }, ref) => (
  <textarea ref={ref} className={cn(controlClass, 'py-2', className)} {...p} />
))
Textarea.displayName = 'Textarea'

export function Checkbox({ label, className, ...p }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={cn('inline-flex items-center gap-2 text-[13px] text-ink-100 select-none cursor-pointer', className)}>
      <input type="checkbox" className="h-4 w-4 rounded border-cream-300 accent-ginger-500 text-ginger-500 cursor-pointer" {...p} />
      {label}
    </label>
  )
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange(v: boolean): void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn('relative h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-50', checked ? 'bg-ginger-500' : 'bg-cream-300')}
    >
      {/* The knob stays pure white in both themes, like the mockup switch (meow.css .sw::after). */}
      <span className={cn('absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-[#FFFFFF] shadow-xs transition-transform', checked ? 'translate-x-4' : 'translate-x-0')} />
    </button>
  )
}

/** Segmented control / tabs. */
export function Segmented<T extends string>({ value, onChange, options, label, className }: { value: T; onChange(v: T): void; options: { value: T; label: ReactNode }[]; label: string; className?: string }) {
  return (
    <div role="tablist" aria-label={label} className={cn('scrollbar-none inline-flex max-w-full overflow-x-auto overflow-y-hidden rounded-[9px] bg-cream-200 p-[3px] gap-0.5', className)}>
      {options.map(o => (
        <button
          key={o.value} type="button" role="tab" aria-selected={o.value === value} onClick={() => onChange(o.value)}
          className={cn('h-[30px] whitespace-nowrap rounded-[7px] px-3 text-[13px] font-medium max-sm:h-[38px] max-sm:text-sm transition-colors', o.value === value ? 'bg-cream-50 text-ink-100 ring-1 ring-line shadow-xs' : 'text-ink-300 hover:text-ink-100')}
        >{o.label}</button>
      ))}
    </div>
  )
}
